import React, { useState } from 'react';
import axios from 'axios';
import { CURSES, CURSE_LIST, tallyCurseStack, MAX_CURSE_STACK } from '../game/curses';
import { Skull, X, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// v3.12 Inline curse-picker modal launched from the CURSED RUNS leaderboard.
// v3.18: same curse can now be stacked (see /app/frontend/src/game/curses.js MAX_CURSE_STACK=100).
export default function CurseInlineModal({ runCode, playerTag, existingCurseIds, onClose, onDone }) {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const existing = existingCurseIds || [];
  const stackFull = existing.length >= MAX_CURSE_STACK;
  const stackTally = tallyCurseStack(existing);
  const nextLayer = existing.length + 1;
  const nextMult = 1 + (nextLayer - 1) * 0.5;
  const preview = selected
    ? Math.floor(((CURSES[selected]?.tokenBonus) || 0) * nextMult)
    : 0;

  const commit = async () => {
    if (!selected || stackFull) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/curses`, {
        run_code: runCode,
        curse_id: selected,
        cursed_by: playerTag || 'ANON',
        original_score: 0,
      });
      if (onDone) onDone();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'FAILED TO CURSE');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      data-testid="curse-inline-modal"
    >
      <div
        className="brutal-border-pink bg-[#0a0a0a] p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--pink)]">
            <Skull size={14}/> STACK A CURSE ON <span className="text-white font-bold">{runCode}</span>
          </div>
          <button
            className="brutal-btn text-xs px-2 py-1"
            onClick={onClose}
            data-testid="curse-inline-close"
          ><X size={12}/></button>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] mb-3 flex items-center gap-3 flex-wrap">
          <span>NEXT LAYER {nextLayer} · <span className="text-[color:var(--neon)]">{nextMult}× multiplier</span></span>
          <span data-testid="curse-inline-stack-count">
            STACK: <span className="text-white font-bold">{existing.length}</span>/<span>{MAX_CURSE_STACK}</span>
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {CURSE_LIST.map(cid => {
            const c = CURSES[cid];
            const count = stackTally.get(cid) || 0;
            const active = selected === cid;
            return (
              <button
                key={cid}
                onClick={() => !stackFull && setSelected(cid)}
                className={`text-left p-3 border-2 transition-all ${active ? 'bg-[#160a10]' : stackFull ? 'bg-[#050505] opacity-40' : 'bg-[#080808] hover:bg-[#0e0e0e]'}`}
                style={{
                  borderColor: active ? c.color : stackFull ? '#333' : 'var(--muted)',
                  boxShadow: active ? `4px 4px 0 ${c.color}` : 'none',
                  cursor: stackFull ? 'not-allowed' : 'pointer',
                }}
                disabled={stackFull}
                data-testid={`curse-inline-option-${cid}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{c.art}</span>
                  <span className="font-display font-black uppercase text-sm" style={{color: c.color}}>{c.name}</span>
                  {count > 0 && (
                    <span
                      className="text-[10px] uppercase font-black px-1.5 py-0.5 ml-auto"
                      style={{ border: `1px solid ${c.color}`, color: c.color, background: `${c.color}22` }}
                      data-testid={`curse-inline-count-${cid}`}
                    >×{count}</span>
                  )}
                </div>
                <div className="text-[10px] text-[color:var(--text-dim)] mt-1">{c.desc}</div>
                <div className="text-[10px] text-white/70 mt-1">Base +{c.tokenBonus}T · Preview +{Math.floor(c.tokenBonus * nextMult)}T</div>
              </button>
            );
          })}
        </div>
        {err && <div className="mt-3 text-xs uppercase text-[color:var(--red)]" data-testid="curse-inline-error">{err}</div>}
        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-widest text-[color:var(--neon)]">
            {stackFull ? 'STACK FULL — CANNOT ADD MORE' : selected ? `+${preview} BONUS TOKENS` : 'PICK A CURSE TO STACK'}
          </div>
          <button
            className="brutal-btn-pink brutal-btn text-sm"
            onClick={commit}
            disabled={!selected || busy || stackFull}
            data-testid="curse-inline-commit"
          ><Zap size={14}/> {busy ? 'PLANTING...' : stackFull ? 'STACK FULL' : 'STACK IT'}</button>
        </div>
      </div>
    </div>
  );
}
